import React, { useEffect, useState } from 'react';
import { Heart } from 'lucide-react';
import { Modal } from '../UI/Modal';
import { albumsApi } from '../../api/albums';
import { Skeleton } from '../UI/Skeleton';
import styles from './LikersModal.module.css';

interface LikersModalProps {
  isOpen: boolean;
  onClose: () => void;
  albumId: string;
  photoId: string;
}

type LikesData = Awaited<ReturnType<typeof albumsApi.getPhotoLikes>>;

function formatRelative(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString();
}

export function LikersModal({ isOpen, onClose, albumId, photoId }: LikersModalProps) {
  const [data, setData] = useState<LikesData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOpen || !photoId) return;
    setLoading(true);
    setData(null);
    albumsApi
      .getPhotoLikes(albumId, photoId)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [isOpen, albumId, photoId]);

  const anonymousCount = data ? data.totalCount - data.publicCount : 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Likes" size="sm">
      <div className={styles['container']}>
        {loading ? (
          <div className={styles['skeleton-list']}>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className={styles['skeleton-row']}>
                <Skeleton style={{ width: 32, height: 32, borderRadius: '50%' }} />
                <div className={styles['skeleton-text-group']}>
                  <Skeleton className={styles['skeleton-name']} />
                  <Skeleton className={styles['skeleton-time']} />
                </div>
              </div>
            ))}
          </div>
        ) : !data ? (
          <p className={styles['error-text']}>
            Failed to load likes
          </p>
        ) : data.totalCount === 0 ? (
          <div className={styles['empty-state']}>
            <Heart style={{ width: 32, height: 32 }} className={styles['empty-icon']} />
            <p className={styles['empty-text']}>
              No likes yet
            </p>
          </div>
        ) : (
          <div className={styles['likers-list']}>
            {/* Public likers list */}
            {data.likes.length > 0 && (
              <div className={styles['likers-scroll']}>
                {data.likes.map((liker) => (
                  <div
                    key={liker.userId}
                    className={styles['liker-row']}
                  >
                    <div className={styles['liker-info']}>
                      {/* Initial avatar */}
                      <div className={styles['liker-avatar']}>
                        {liker.name.charAt(0).toUpperCase()}
                      </div>
                      <span className={styles['liker-name']}>
                        {liker.name}
                      </span>
                    </div>
                    <span className={styles['liker-time']}>
                      {formatRelative(liker.likedAt)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Anonymous likes footer */}
            {anonymousCount > 0 && (
              <div className={styles['anonymous-footer']}>
                <p className={styles['anonymous-text']}>
                  and {anonymousCount} anonymous like{anonymousCount !== 1 ? 's' : ''}
                </p>
              </div>
            )}

            {/* Edge case: all anonymous */}
            {data.likes.length === 0 && anonymousCount > 0 && (
              <div className={styles['all-anonymous']}>
                <Heart style={{ width: 24, height: 24 }} className={styles['all-anonymous-icon']} />
                <p className={styles['all-anonymous-text']}>
                  {data.totalCount} like{data.totalCount !== 1 ? 's' : ''} (all anonymous)
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
